// Tooltip.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react'
import styled from 'styled-components'

interface TooltipProps {
  text: string
  children: React.ReactNode
  position?: 'top' | 'bottom'
}

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`

const TooltipText = styled.div<{ isVisible: boolean; position: string }>`
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  min-width: 100px;
  max-width: 200px;
  width: max-content;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 8px 12px;
  position: absolute;
  z-index: 9999;
  font-size: 12px;
  line-height: 1.4;

  left: 50%;
  transform: translateX(-50%);
  ${(props) => props.position === 'top' ? 'bottom: 100%; margin-bottom: 8px;' : 'top: 100%; margin-top: 8px;'}

  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.2s, visibility 0.2s;

  /* Mobile responsive */
  @media (max-width: 767px) {
    min-width: 80px;
    max-width: 150px;
    font-size: 11px;
    padding: 6px 10px;
  }

  &:after {
    content: '';
    position: absolute;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    ${(props) => props.position === 'top'
      ? 'top: 100%; border-color: black transparent transparent transparent;'
      : 'bottom: 100%; border-color: transparent transparent black transparent;'}
  }
`

const Tooltip: React.FC<TooltipProps> = ({ text, children, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const showTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(true)
  }, [])

  const hideTooltip = useCallback(() => {
    setIsVisible(false)
  }, [])

  // Auto-hide after 2 seconds on touch
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation()
    showTooltip()
    timeoutRef.current = setTimeout(hideTooltip, 2000)
  }, [showTooltip, hideTooltip])

  // Toggle on click for mobile
  const handleClick = useCallback((e: React.MouseEvent) => {
    // Only toggle on touch devices
    if ('ontouchstart' in window) {
      e.preventDefault()
      e.stopPropagation()
      if (isVisible) {
        hideTooltip()
      } else {
        showTooltip()
        timeoutRef.current = setTimeout(hideTooltip, 2000)
      }
    }
  }, [isVisible, showTooltip, hideTooltip])

  return (
    <TooltipContainer
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      {children}
      <TooltipText isVisible={isVisible} position={position}>{text}</TooltipText>
    </TooltipContainer>
  )
}

export default Tooltip
